/**
 * Trip Request Detail Page Component
 * /admin/trip-requests/:id
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import apiClient from '../api/client';

const TripRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tripRequest, setTripRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [converting, setConverting] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // The endpoint requires a pricing plan for a direct customer: a corporate
  // one is priced from its route price list, a direct one has nothing to
  // price from. The dialog used to collect nothing and post an empty body,
  // so converting a direct customer's request always failed with
  // "pricing_plan_id is required for direct customers".
  const [pricingPlans, setPricingPlans] = useState([]);
  const [pricingPlanId, setPricingPlanId] = useState('');
  const [serviceType, setServiceType] = useState('limousine');

  useEffect(() => {
    loadTripRequest();
    loadPricingPlans();
  }, [id]);

  const loadPricingPlans = async () => {
    try {
      const response = await apiClient.getPricingPlans({ is_active: true });
      const plans = Array.isArray(response)
        ? response
        : response.results || response.data || [];
      setPricingPlans(plans);
    } catch (err) {
      // Not fatal: a corporate request converts without one, and the dialog
      // says so rather than silently offering an empty list.
      setPricingPlans([]);
    }
  };

  const loadTripRequest = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getTripRequest(id);
      setTripRequest(data);
    } catch (err) {
      setError(err.message || 'Failed to load trip request');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToBooking = async () => {
    try {
      setConverting(true);
      setError('');
      const payload = { service_type: serviceType };
      if (pricingPlanId) {
        payload.pricing_plan_id = pricingPlanId;
      }
      const response = await apiClient.convertTripRequestToBooking(id, payload);
      setSuccessMessage(`Trip request converted to booking successfully! Booking ID: ${response.booking?.booking_number || response.booking?.id}`);
      setConvertDialogOpen(false);
      // Reload trip request to get updated data
      await loadTripRequest();
      // Optionally navigate to booking detail page
      if (response.booking?.id) {
        setTimeout(() => {
          navigate(`/admin/bookings`);
        }, 2000);
      }
    } catch (err) {
      setError(err.message || 'Failed to convert trip request to booking');
      setConvertDialogOpen(false);
    } finally {
      setConverting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      assigned: 'info',
      confirmed: 'primary',
      active: 'success',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      normal: 'default',
      high: 'warning',
      urgent: 'error',
    };
    return colors[priority] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  /**
   * Readable form of a structured address, or null when there is nothing.
   *
   * pickup_address and dropoff_address default to an empty object, and `{}` is
   * truthy, so a plain `address && ...` guard rendered JSON.stringify({}) -
   * a literal "{}" under every location. Returning null lets the caller skip
   * the line entirely, and the surviving values are joined as text rather
   * than shown as raw JSON.
   */
  const formatAddress = (address) => {
    if (!address || typeof address !== 'object') return null;
    const parts = Object.values(address)
      .filter((value) => typeof value === 'string' || typeof value === 'number')
      .map((value) => String(value).trim())
      .filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !tripRequest) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/trip-requests')}>
          Back to Trip Requests
        </Button>
      </Container>
    );
  }

  if (!tripRequest) {
    return null;
  }

  const canConvert = tripRequest.status === 'pending' && !tripRequest.booking;
  // A corporate booking is priced from the customer's route price list; a
  // direct one has no list to price from, so the endpoint requires a plan.
  const isCorporate =
    (tripRequest.request_profile || tripRequest.customer_type) === 'corporate';

  return (
    <Container maxWidth="lg">
      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/trip-requests')}
            sx={{ mb: 1 }}
          >
            Back to Trip Requests
          </Button>
          <Typography variant="h4" component="h1" gutterBottom>
            Trip Request Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reference: {tripRequest.trip_reference || tripRequest.id.substring(0, 8)}
          </Typography>
        </Box>
        {canConvert && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
            onClick={() => setConvertDialogOpen(true)}
            disabled={converting}
          >
            Convert to Booking
          </Button>
        )}
        {tripRequest.booking && (
          <Chip
            label={`Converted to Booking: ${tripRequest.booking_detail?.booking_number || tripRequest.booking}`}
            color="success"
            size="medium"
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Timing the customer entered that looks inconsistent. Shown, never
          silently corrected - the entered values are preserved as given. */}
      {(tripRequest.warnings || []).length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Timing needs attention</AlertTitle>
          {tripRequest.warnings.map((warning) => (
            <Typography key={`${warning.code}-${warning.leg_sequence}`} variant="body2">
              Leg {warning.leg_sequence}: {warning.message}
              {warning.acknowledged ? ' (acknowledged)' : ''}
            </Typography>
          ))}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Journey classification */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Journey
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <Chip
                size="small"
                color={tripRequest.request_profile === 'corporate' ? 'primary' : 'default'}
                label={tripRequest.request_profile === 'corporate' ? 'Corporate' : 'Direct'}
              />
              <Chip
                size="small"
                label={tripRequest.journey_type === 'round_trip' ? 'Round Trip' : 'One Way'}
              />
              {tripRequest.round_trip_mode && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    tripRequest.round_trip_mode === 'continuous_with_wait'
                      ? 'Continuous — vehicle waits'
                      : 'Split date return'
                  }
                />
              )}
              {tripRequest.expected_waiting_hours != null && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Expected waiting: ${tripRequest.expected_waiting_hours} h`}
                />
              )}
              <Chip
                size="small"
                variant="outlined"
                label={`${tripRequest.declared_traveller_count || 0} traveller(s): ${
                  tripRequest.named_traveller_count || 0
                } named, ${tripRequest.pending_traveller_count || 0} pending`}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              A Trip Request records demand only. No vehicle or driver has been assigned.
            </Typography>
          </Paper>
        </Grid>

        {/* Travellers */}
        {(tripRequest.travellers || []).length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Travellers
              </Typography>
              <Divider sx={{ my: 1 }} />
              {tripRequest.travellers
                .filter((t) => t.is_active !== false)
                .map((traveller) => (
                  <Box key={traveller.id} sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      {traveller.full_name || <em>Name not yet provided</em>}
                      {traveller.is_primary ? ' — primary' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {traveller.traveller_category}
                      {traveller.traveller_relationship ? ` · ${traveller.traveller_relationship}` : ''}
                      {traveller.contact_number ? ` · ${traveller.contact_number}` : ''}
                      {traveller.employee_reference ? ` · ${traveller.employee_reference}` : ''}
                    </Typography>
                  </Box>
                ))}
            </Paper>
          </Grid>
        )}

        {/* Attachments */}
        {(tripRequest.attachments || []).length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Supporting Documents
              </Typography>
              <Divider sx={{ my: 1 }} />
              {tripRequest.attachments
                .filter((a) => a.is_active !== false)
                .map((attachment) => (
                  <Box key={attachment.id} sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      {attachment.original_filename}
                      <Chip size="small" sx={{ ml: 1 }} label={attachment.document_type} />
                    </Typography>
                    {attachment.description && (
                      <Typography variant="caption" color="text.secondary">
                        {attachment.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              <Typography variant="caption" color="text.secondary">
                Details only — file storage is not yet available in this build.
              </Typography>
            </Paper>
          </Grid>
        )}

        {/* Ordered service legs */}
        {(tripRequest.legs || []).length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Service Legs
              </Typography>
              <Divider sx={{ my: 1 }} />
              {tripRequest.legs
                .filter((leg) => leg.is_active !== false)
                .map((leg) => (
                  <Box key={leg.id} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      {leg.leg_role === 'outbound'
                        ? 'Outbound'
                        : leg.leg_role === 'return'
                        ? 'Return'
                        : `Additional leg ${leg.sequence}`}
                      {' — '}
                      {leg.service_type}
                    </Typography>
                    <Typography variant="body2">
                      {leg.pickup_location} → {leg.dropoff_location}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Requested Vehicle Pickup: {formatDate(leg.requested_pickup_datetime)}
                      {' · '}
                      {leg.expected_passenger_count} passenger(s)
                    </Typography>
                    {(leg.flight_number || leg.scheduled_flight_arrival ||
                      leg.scheduled_flight_departure) && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Flight {leg.airline || ''} {leg.flight_number || ''}
                        {leg.departure_airport ? ` ${leg.departure_airport}` : ''}
                        {leg.arrival_airport ? ` → ${leg.arrival_airport}` : ''}
                        {leg.scheduled_flight_departure
                          ? ` · Scheduled Flight Departure: ${formatDate(leg.scheduled_flight_departure)}`
                          : ''}
                        {leg.scheduled_flight_arrival
                          ? ` · Scheduled Flight Arrival: ${formatDate(leg.scheduled_flight_arrival)}`
                          : ''}
                        {leg.ticket_baggage_allowance_kg
                          ? ` · Ticket allowance ${leg.ticket_baggage_allowance_kg} kg`
                          : ''}
                      </Typography>
                    )}
                    {(leg.load_items || []).filter((i) => i.is_active !== false).length > 0 && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Expected Actual Luggage:{' '}
                        {leg.load_items
                          .filter((i) => i.is_active !== false)
                          .map((item) => `${item.quantity} × ${item.item_category}`)
                          .join(', ')}
                      </Typography>
                    )}
                  </Box>
                ))}
            </Paper>
          </Grid>
        )}

        {/* Status and Priority */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Status & Priority
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip
                label={`Status: ${tripRequest.status || 'pending'}`}
                color={getStatusColor(tripRequest.status)}
                size="medium"
              />
              <Chip
                label={`Priority: ${tripRequest.priority || 'normal'}`}
                color={getPriorityColor(tripRequest.priority)}
                size="medium"
              />
            </Box>
          </Paper>
        </Grid>

        {/* Customer Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Type: {tripRequest.customer_type === 'corporate' ? 'Corporate' : 'Direct'}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {tripRequest.customer_detail?.customer_name || tripRequest.customer_name || '-'}
            </Typography>
            {tripRequest.customer_phone && (
              <Typography variant="body2" color="text.secondary">
                Phone: {tripRequest.customer_phone}
              </Typography>
            )}
            {tripRequest.customer_email && (
              <Typography variant="body2" color="text.secondary">
                Email: {tripRequest.customer_email}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Trip Details */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Pickup Location
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body1">
              {tripRequest.pickup_location || '-'}
            </Typography>
            {formatAddress(tripRequest.pickup_address) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {formatAddress(tripRequest.pickup_address)}
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Dropoff Location
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body1">
              {tripRequest.dropoff_location || '-'}
            </Typography>
            {formatAddress(tripRequest.dropoff_address) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {formatAddress(tripRequest.dropoff_address)}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Schedule */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Schedule
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Scheduled Date & Time:
            </Typography>
            <Typography variant="body1">
              {formatDate(tripRequest.scheduled_datetime)}
            </Typography>
            {tripRequest.estimated_duration && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Estimated Duration:
                </Typography>
                <Typography variant="body1">
                  {tripRequest.estimated_duration} minutes
                </Typography>
              </>
            )}
            {tripRequest.estimated_distance && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Estimated Distance:
                </Typography>
                <Typography variant="body1">
                  {tripRequest.estimated_distance} km
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Additional Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Additional Information
            </Typography>
            <Divider sx={{ my: 1 }} />
            {tripRequest.preferred_vehicle_type && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Preferred Vehicle Type:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {tripRequest.preferred_vehicle_type}
                </Typography>
              </>
            )}
            {tripRequest.special_requirements && tripRequest.special_requirements.length > 0 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Special Requirements:
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {tripRequest.special_requirements.map((req, index) => (
                    <Chip key={index} label={req} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </Box>
              </>
            )}
            {tripRequest.notes && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Notes:
                </Typography>
                <Typography variant="body1">
                  {tripRequest.notes}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Booking Information (if converted) */}
        {tripRequest.booking && tripRequest.booking_detail && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Related Booking
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body1">
                Booking Number: {tripRequest.booking_detail.booking_number || tripRequest.booking}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status: {tripRequest.booking_detail.status || '-'}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
                onClick={() => navigate(`/admin/bookings`)}
              >
                View Booking
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Convert to Booking Dialog */}
      <Dialog
        open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        aria-labelledby="convert-dialog-title"
      >
        <DialogTitle id="convert-dialog-title">
          Convert Trip Request to Booking
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Converting this trip request will:
            <ul>
              <li>Create a new booking from this trip request</li>
              <li>Calculate the price based on the customer's price list (if corporate)</li>
              <li>Set the booking status to "pending"</li>
              <li>Link the booking to this trip request</li>
            </ul>
            This action cannot be undone.
          </DialogContentText>

          <TextField
            select
            fullWidth
            margin="normal"
            label="Service Type"
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
          >
            <MenuItem value="limousine">Limousine Service</MenuItem>
            <MenuItem value="rental">Rental (Rent-a-Car)</MenuItem>
            <MenuItem value="equipment">Equipment Rental</MenuItem>
            <MenuItem value="school">School Transport</MenuItem>
          </TextField>

          {/* Required for a direct customer; optional for a corporate one,
              whose price comes from its route price list. */}
          <TextField
            select
            fullWidth
            margin="normal"
            label="Pricing Plan"
            value={pricingPlanId}
            onChange={(e) => setPricingPlanId(e.target.value)}
            required={!isCorporate}
            error={!isCorporate && !pricingPlanId}
            helperText={
              isCorporate
                ? "Optional. A corporate booking is priced from the customer's route price list."
                : pricingPlans.length === 0
                ? 'No active pricing plans exist. Create one before converting a direct request.'
                : 'Required for a direct customer.'
            }
          >
            {isCorporate && <MenuItem value="">Use the customer&apos;s price list</MenuItem>}
            {pricingPlans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.name}
                {plan.service_type ? ` — ${plan.service_type}` : ''}
                {plan.base_rate ? ` (${plan.base_rate}/${plan.unit || 'trip'})` : ''}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)} disabled={converting}>
            Cancel
          </Button>
          <Button
            onClick={handleConvertToBooking}
            variant="contained"
            color="primary"
            disabled={converting || (!isCorporate && !pricingPlanId)}
            startIcon={converting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {converting ? 'Converting...' : 'Convert to Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TripRequestDetail;

